import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography, Flex } from 'antd';
import { CloseOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import styles from './test-configuration-modal.module.scss';

const { Title, Paragraph, Text } = Typography;

/**
 * @typedef {Object} TestStep
 * @property {string} id - Unique identifier for the test step
 * @property {string} label - Display text for the test step
 * @property {('pending'|'passed'|'failed')} status - Current status of the test step
 */

/**
 * Modal component that simulates testing queue configuration settings
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Controls whether the modal is visible
 * @param {Function} props.onClose - Callback function when the modal is closed
 * @param {Object} props.settings - Queue configuration settings to test
 * @returns {React.ReactElement} Test Configuration Modal component
 */
const TestConfigurationModal = ({ visible, onClose, settings }) => {
  const [testSteps, setTestSteps] = useState([
    { id: 'distribution', label: 'Queue distribution method validated', status: 'pending' },
    { id: 'team-members', label: 'Team member statuses correctly configured', status: 'pending' },
    { id: 'after-hours', label: 'After hours handling properly set up', status: 'pending' },
    { id: 'reassignment', label: 'Lead reassignment timing validated', status: 'pending' },
  ]);

  const [currentStep, setCurrentStep] = useState(0);
  const [testingComplete, setTestingComplete] = useState(false);
  const [testingInProgress, setTestingInProgress] = useState(false);

  /**
   * Effect hook to start the testing process when the modal becomes visible
   * Simulates a series of tests for the queue configuration
   */
  useEffect(() => {
    if (visible && !testingComplete) {
      setTestingInProgress(true);

      // Reset test steps when modal opens
      setTestSteps(testSteps.map((step) => ({ ...step, status: 'pending' })));
      setCurrentStep(0);

      // Simulate testing process with delays
      const runTests = async () => {
        // Test distribution method
        await simulateTest(0);

        // Test team member statuses
        await simulateTest(1);

        // Test after hours handling
        await simulateTest(2);

        // Test reassignment timing
        await simulateTest(3);

        setTestingComplete(true);
        setTestingInProgress(false);
      };

      runTests();
    }
  }, [visible]);

  /**
   * Effect hook to reset the component state when the modal is closed
   * Ensures the modal starts fresh on next open
   */
  useEffect(() => {
    if (!visible) {
      setTimeout(() => {
        setTestingComplete(false);
        setTestSteps(testSteps.map((step) => ({ ...step, status: 'pending' })));
      }, 300);
    }
  }, [visible]);

  /**
   * Simulates running a test for a specific step
   *
   * @param {number} stepIndex - Index of the test step to simulate
   * @returns {Promise<void>} Promise that resolves when the test is complete
   */
  const simulateTest = async (stepIndex) => {
    setCurrentStep(stepIndex);

    // Simulate a test that takes between 1-2 seconds
    await new Promise((resolve) => setTimeout(resolve, 1000 + Math.random() * 1000));

    // Update the step status to passed
    setTestSteps((prevSteps) => {
      const newSteps = [...prevSteps];
      newSteps[stepIndex] = { ...newSteps[stepIndex], status: 'passed' };
      return newSteps;
    });
  };

  /**
   * Renders an individual test step with its current status
   *
   * @param {TestStep} step - The test step to render
   * @param {number} index - Index of the step in the steps array
   * @returns {React.ReactElement} Rendered test step
   */
  const renderTestStep = (step, index) => {
    return (
      <div key={step.id} className={styles.testStep}>
        <Text className={styles.testLabel}>{step.label}</Text>
        <div className={styles.testStatus}>
          {currentStep === index && step.status === 'pending' && testingInProgress ? (
            <div className={styles.runningTest}>Running test...</div>
          ) : step.status === 'passed' ? (
            <div className={styles.passedBadge}>Passed</div>
          ) : (
            <div className={styles.pendingBadge} />
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      footer={null}
      closable
      onCancel={onClose}
      closeIcon={<CloseOutlined />}
      width={600}
      className={styles.testConfigModal}
      maskClosable={false}>
      <Title level={2} className={styles.modalTitle}>
        Testing Queue Configuration
      </Title>
      <Paragraph className={styles.modalDescription}>
        Validating your current queue settings before applying them system-wide.
      </Paragraph>

      <div className={styles.testStepsContainer}>{testSteps.map(renderTestStep)}</div>

      <Flex justify="flex-end" className={styles.modalFooter}>
        {testingComplete ? (
          <Button type="primary" onClick={onClose} className={styles.closeButton}>
            Close
          </Button>
        ) : (
          <Button type="primary" className={styles.testingButton} disabled>
            Testing...
          </Button>
        )}
      </Flex>
    </Modal>
  );
};

// PropTypes validation
TestConfigurationModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  settings: PropTypes.object,
};

export default TestConfigurationModal;
